import { useEffect } from "react";
import { useNavigate, useSearchParams, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Alert, Button, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { authV2InvitationStatus, authV2Selector, AUTH_REQUEST_KEYS } from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { MiniLoader } from "@/components/Loader";
import { buildPath } from "@/utils/buildPath";
import { useAuthInviteStep } from "./hooks";
import { getInvitationTitle } from "./utils";

let hasFetchedInvitation = false;

const AuthInviteLayout = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token =
    searchParams.get("token") ?? searchParams.get("invitationToken") ?? searchParams.get("invitation_token") ?? "";
  const authV2 = useSelector(authV2Selector);
  const invitationStatus = authV2?.invitationStatus;
  const invitationEmail = invitationStatus?.invitation?.email;
  const loadingInvitationStatus = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.invitationStatus]);
  const { navigateToStep } = useAuthInviteStep();
  const isInviteAcceptPath = location.pathname === "/auth/invite/accept";
  const requiresSignInToAccept = invitationStatus?.account?.requiresSignInToAccept;
  const isAuthenticated = Boolean(authV2?.session?.authenticated || sessionStorage.token);
  const shouldRedirectToSignIn = Boolean(requiresSignInToAccept && !isAuthenticated);
  const canAcceptAfterSignIn = Boolean(isInviteAcceptPath && requiresSignInToAccept && isAuthenticated);

  const invitationTitle = getInvitationTitle({
    tenancyName: invitationStatus?.invitation?.tenancyName ?? null,
    organisationName: invitationStatus?.invitation?.organisationName ?? null,
  });

  useEffect(() => {
    if (!token || hasFetchedInvitation) return;
    hasFetchedInvitation = true;
    dispatch(authV2InvitationStatus({ token }));
  }, []);

  useEffect(() => {
    if (shouldRedirectToSignIn) {
      const redirectTimeoutId = window.setTimeout(() => {
        navigate(
          buildPath("/auth/sign-in", {
            ...(invitationEmail ? { email: invitationEmail } : {}),
            invitationToken: token,
          }),
        );
      }, 6000);

      return () => window.clearTimeout(redirectTimeoutId);
    }

    if (!invitationStatus?.setup || canAcceptAfterSignIn) return;
    navigateToStep();
  }, [
    canAcceptAfterSignIn,
    invitationEmail,
    invitationStatus?.setup,
    shouldRedirectToSignIn,
    navigateToStep,
    navigate,
    token,
  ]);

  const navigateBack = () => {
    navigate(-1);
  };

  if (loadingInvitationStatus) {
    return <MiniLoader />;
  }

  return (
    <>
      {!token ? (
        <Stack textAlign="center" rowGap={1}>
          <Typography level="title-lg">Oops, this isn't right</Typography>
          <Typography level="body-md">We couldn't find your invitation</Typography>
        </Stack>
      ) : (
        <Stack textAlign="center" rowGap={1}>
          <Typography level="title-lg">Accept your invitation</Typography>
          <Typography level="body-md">{invitationTitle}</Typography>
        </Stack>
      )}
      {shouldRedirectToSignIn ? (
        <Alert
          intent="neutral"
          variant="filled"
          description="We found an existing user for this email address. We're redirecting you to sign in now."
        />
      ) : (
        <Outlet />
      )}
      <Button
        startDecorator={<ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />}
        variant="inline"
        intent="neutral"
        size="lg"
        onClick={navigateBack}
      >
        Go back
      </Button>
    </>
  );
};

export { AuthInviteLayout };
